import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { WarningCircle, Trash, Info, CheckCircle } from "@phosphor-icons/react";

const ConfirmCtx = createContext(null);

const TONES = {
  danger: {
    ring: "ring-red-500",
    iconBg: "bg-red-100",
    iconClr: "text-red-600",
    btnBg: "bg-red-600 hover:bg-red-700",
    Icon: Trash,
    accent: "border-t-4 border-red-600",
  },
  warning: {
    ring: "ring-yellow-500",
    iconBg: "bg-yellow-100",
    iconClr: "text-yellow-700",
    btnBg: "bg-yellow-600 hover:bg-yellow-700",
    Icon: WarningCircle,
    accent: "border-t-4 border-yellow-500",
  },
  info: {
    ring: "ring-blue-500",
    iconBg: "bg-blue-100",
    iconClr: "text-blue-700",
    btnBg: "bg-blue-600 hover:bg-blue-700",
    Icon: Info,
    accent: "border-t-4 border-blue-500",
  },
  success: {
    ring: "ring-green-500",
    iconBg: "bg-green-100",
    iconClr: "text-green-700",
    btnBg: "bg-green-600 hover:bg-green-700",
    Icon: CheckCircle,
    accent: "border-t-4 border-green-500",
  },
};

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title: opts.title || "Are you sure?",
        message: opts.message || "",
        tone: opts.tone || "danger",
        confirmLabel: opts.confirmLabel || (opts.tone === "info" ? "OK" : "Delete"),
        cancelLabel: opts.cancelLabel || "Cancel",
        hideCancel: opts.hideCancel || false,
        confirmText: opts.confirmText || null,  // if set, user must type this to confirm
      });
    });
  }, []);

  // Convenience shorthand: alert(message) → single-button info
  const alert = useCallback((message, title = "Notice") => confirm({
    title, message, tone: "info", confirmLabel: "OK", hideCancel: true,
  }), [confirm]);

  const close = (result) => {
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
    setState(null);
  };

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert]);

  return (
    <ConfirmCtx.Provider value={value}>
      {children}
      {state && <ConfirmModal state={state} onClose={close} />}
    </ConfirmCtx.Provider>
  );
}

function ConfirmModal({ state, onClose }) {
  const t = TONES[state.tone] || TONES.danger;
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const canProceed = !state.confirmText || typed.trim() === state.confirmText;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose(false);
      if (e.key === "Enter" && !state.confirmText && !busy) onClose(true);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [state.confirmText, busy, onClose]);

  const handleConfirm = () => {
    if (!canProceed) return;
    setBusy(true);
    onClose(true);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadein"
      data-testid="confirm-backdrop"
      onClick={() => onClose(false)}
    >
      <div
        className={`relative bg-white shadow-2xl w-full max-w-md ${t.accent} animate-scalein`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="confirm-dialog"
      >
        <div className="p-6">
          <div className={`w-14 h-14 rounded-full ${t.iconBg} flex items-center justify-center mb-4`}>
            <t.Icon size={26} weight="bold" className={t.iconClr} />
          </div>
          <h3 className="text-xl font-black tracking-tight mb-2" data-testid="confirm-title">{state.title}</h3>
          {state.message && (
            <p className="text-sm text-zinc-600 leading-relaxed whitespace-pre-wrap" data-testid="confirm-message">
              {state.message}
            </p>
          )}
          {state.confirmText && (
            <div className="mt-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                Type <span className="text-red-600 font-bold">{state.confirmText}</span> to confirm
              </div>
              <input
                autoFocus
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="w-full border-2 border-zinc-300 focus:border-red-500 focus:outline-none px-3 py-2 font-mono"
                data-testid="confirm-input"
              />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 bg-zinc-50 border-t border-zinc-200">
          {!state.hideCancel && (
            <button
              type="button"
              onClick={() => onClose(false)}
              disabled={busy}
              className="px-4 py-2 bg-white border border-zinc-300 hover:bg-zinc-100 text-sm font-bold uppercase tracking-widest disabled:opacity-50"
              data-testid="confirm-cancel"
            >
              {state.cancelLabel}
            </button>
          )}
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canProceed || busy}
            className={`px-5 py-2 ${t.btnBg} text-white text-sm font-bold uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed`}
            data-testid="confirm-ok"
            autoFocus={!state.confirmText}
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmCtx);
  if (!ctx) {
    // Fallback to native so refactor is incremental-safe
    return {
      // eslint-disable-next-line no-alert
      confirm: async (opts) => window.confirm(opts.message || opts.title || "Are you sure?"),
      // eslint-disable-next-line no-alert
      alert: async (msg) => window.alert(msg),
    };
  }
  return ctx;
}
